import { GetSharepointWorkbookContextAction } from 'constants/sharepoint';
import { Maybe } from 'types/UtilityTypes';

/** Subset of EWA fields cached in the service worker (see `sharepoint/workbook-context`). */
export type SharepointEwaWorkbookContext = {
  activeSheetName?: string;
  activeNamedObject?: string;
  activeSheetId?: string | number;
  activeSheetIndex?: number;
};

/**
 * Reads the last Excel Online active-sheet hint for the host tab (from prefetch intercept).
 * Returns null if unavailable — caller should fall back to server default worksheet selection.
 */
export async function getSharepointWorkbookEwaContext(
  hostTabId: number
): Promise<SharepointEwaWorkbookContext | null> {
  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
    return null;
  }
  return new Promise((resolve) => {
    try {
      chrome.runtime.sendMessage(
        {
          action: GetSharepointWorkbookContextAction,
          tabId: hostTabId,
        },
        (response: {
          success?: boolean;
          workbookContext?: SharepointEwaWorkbookContext | null;
        }) => {
          if (chrome.runtime.lastError) {
            resolve(null);
            return;
          }
          if (!response?.success || response.workbookContext == null) {
            resolve(null);
            return;
          }
          resolve(response.workbookContext);
        }
      );
    } catch {
      resolve(null);
    }
  });
}

/** Prefer visible sheet tab label; named object is a secondary hint used in EWA grid merge. */
export function worksheetNameFromEwaContext(
  ctx: Maybe<SharepointEwaWorkbookContext>
): string | undefined {
  if (ctx == null) {
    return undefined;
  }
  const name = ctx.activeSheetName?.trim();
  if (name) {
    return name;
  }
  const named = ctx.activeNamedObject?.trim();
  if (named) {
    return named;
  }
  return undefined;
}
